import React, { type ReactElement, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { uploadFile } from '~api/services/utils/upload_utils'
import { images } from '~assets/images'
import Avatar, { type AvatarSize } from '~components/internal/Avatar'
import Button from '~components/internal/Button'
import { updateUserInfo } from '~redux/slices/authSlice'
import { AssetTypeEnum, type User } from '~types'

import './ProfileEditWidget.scss'

type ProfileEditWidgetProps = {
  user: User
  size?: AvatarSize | string
  layout?: 'vertical' | 'horizontal'
  buttonVariant?: 'text' | 'outlined' | 'contained'
  buttonColor?: 'primary' | 'secondary'
  buttonClassName?: string
}

const ProfileEditWidget = ({
  user,
  size = 'lg',
  layout = 'vertical',
  buttonVariant = 'outlined',
  buttonColor = 'secondary',
  buttonClassName,
}: ProfileEditWidgetProps): ReactElement => {
  const dispatch = useDispatch()
  const [isUploading, setIsUploading] = useState(false)
  const [profileImage, setProfileImage] = useState<string | null>(user.image)

  const handleProfileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (e.target.files && e.target.files[0]) {
      setIsUploading(true)
      try {
        const file = e.target.files[0]
        const uploadResponse = await uploadFile(
          user.id,
          user.identity_id,
          file,
          AssetTypeEnum.enum.Logo,
          null,
          null,
          false,
        )

        if (uploadResponse && uploadResponse.url) {
          const uploadedUrl = uploadResponse.url
          const editResponse = await apiZodios.core_editContactImage({
            user_id: user.id,
            image: uploadedUrl,
          })

          if (editResponse.image) {
            setProfileImage(editResponse.image)
            dispatch(updateUserInfo({ ...user, image: editResponse.image }))
          }
        }
      } catch (error) {
        console.error('Error uploading profile image:', error)
      } finally {
        setIsUploading(false)
      }
    }
  }

  return (
    <Box className={`profile-edit-widget profile-edit-widget--${layout}`}>
      <Avatar
        src={isUploading ? images.loadingSpinner : profileImage || undefined}
        alt={user.name || undefined}
        size={size}
      />
      <Button
        variant={buttonVariant}
        color={buttonColor}
        component='label'
        loading={isUploading}
        className={buttonClassName}
        sx={{ whiteSpace: 'nowrap' }}
      >
        <input
          type='file'
          onChange={handleProfileChange}
          style={{ display: 'none' }}
          accept='image/*'
        />
        {profileImage ? 'Change profile picture' : 'Upload profile picture'}
      </Button>
    </Box>
  )
}

export default ProfileEditWidget
